import { useState } from "react";
import { Plus, CreditCard } from "lucide-react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import useBillingHandlers from "../../hooks/useBillingHandlers";
import { positionEmails, buildPayAppMailto, openMailto } from "../../lib/jobContacts";
import {
  BillingSummaryCards,
  BillingTableRow,
  PaymentHistorySection,
  PaymentDialog,
  MarkSentDialog,
} from "./billing";

const BillingsTab = ({
  job,
  canManageJobs,
  onDeleteBilling,
  onRefreshJob,
}) => {
  const {
    generatingExcel,
    revising,
    paymentDialogOpen,
    setPaymentDialogOpen,
    selectedBilling,
    paymentForm,
    setPaymentForm,
    recordingPayment,
    markSentDialogOpen,
    setMarkSentDialogOpen,
    markSentForm,
    setMarkSentForm,
    markingSent,
    sortedBillings,
    activeBillings,
    totalBilled,
    totalReceived,
    totalOutstanding,
    handleAddBilling,
    handleEditBilling,
    downloadExcel,
    handleReviseBilling,
    openPaymentDialog,
    handleRecordPayment,
    handleDeletePayment,
    openMarkSentDialog,
    handleMarkSent,
  } = useBillingHandlers(job, onRefreshJob);

  const gcBillingForm = job?.gc_billing_form || false;
  const [gcDialogOpen, setGcDialogOpen] = useState(false);
  const [gcForm, setGcForm] = useState({ amount: "", date_sent: "" });
  const [submittingGc, setSubmittingGc] = useState(false);
  const [editingGcBilling, setEditingGcBilling] = useState(null);

  const toggleGcBillingForm = async (checked) => {
    try {
      await apiClient.put(`/jobs/${job.id}`, { gc_billing_form: checked });
      if (onRefreshJob) onRefreshJob();
      toast.success(checked ? "Switched to GC supplied billing form" : "Switched to standard billing form");
    } catch {
      toast.error("Failed to update billing mode");
    }
  };

  const handleEmailPayApp = (billing) => {
    const emails = positionEmails(job?.contacts, "billing");
    if (emails.length === 0) {
      toast.warning("No Billing contact with an email on this job. Add one under Edit Job → Contact Information first.", {
        duration: 8000,
        id: "pay-app-no-billing-contact",
      });
      return;
    }
    openMailto(buildPayAppMailto(job, billing, emails), "pay-app-mailto-link");
    const notYetSent = (billing.payment_status || "pending") === "pending";
    if (notYetSent) openMarkSentDialog(billing, { useNow: true });
    else toast.success(`Opening email to ${emails.length} Billing contact${emails.length > 1 ? "s" : ""}`);
  };

  const openGcDialog = (billing = null) => {
    if (billing) {
      setEditingGcBilling(billing);
      setGcForm({
        amount: String(billing.current_payment_due || billing.amount || ""),
        date_sent: billing.application_date || billing.date_sent || "",
      });
    } else {
      setEditingGcBilling(null);
      const nextNum = activeBillings.length + 1;
      setGcForm({ amount: "", date_sent: new Date().toISOString().split("T")[0] });
    }
    setGcDialogOpen(true);
  };

  const handleGcSubmit = async () => {
    const amount = parseFloat(gcForm.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (!gcForm.date_sent) { toast.error("Enter a date"); return; }

    setSubmittingGc(true);
    try {
      if (editingGcBilling) {
        await apiClient.put(`/jobs/${job.id}/billings/${editingGcBilling.id}`, {
          amount,
          application_date: gcForm.date_sent,
          date_sent: gcForm.date_sent,
          current_payment_due: amount,
          retainage_percent: 0,
          total_retainage: 0,
        });
        toast.success("Billing updated");
      } else {
        const nextNum = activeBillings.length + 1;
        await apiClient.post(`/jobs/${job.id}/billings`, {
          billing_number: String(nextNum),
          amount,
          application_date: gcForm.date_sent,
          date_sent: gcForm.date_sent,
          current_payment_due: amount,
          retainage_percent: 0,
          total_retainage: 0,
          total_earned_less_retainage: amount,
          total_completed_stored: amount,
          original_contract_sum: job.contract_amount || 0,
          balance_to_finish: (job.contract_amount || 0) - totalBilled - amount,
          status: "active",
          payment_status: "pending",
        });
        toast.success("Billing added");
      }

      setGcDialogOpen(false);
      if (onRefreshJob) onRefreshJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save billing");
    } finally {
      setSubmittingGc(false);
    }
  };

  // Use estimate_total which includes change orders (effective contract)
  const effectiveContract = job?.estimate_total || job?.contract_amount || 0;
  const remainingToBill = effectiveContract - totalBilled;

  return (
    <div className="space-y-6">
      {/* GC Billing Form Toggle */}
      {canManageJobs && (
        <div className="flex items-center gap-3 px-1" data-testid="gc-billing-toggle">
          <Checkbox
            id="gc-billing-form"
            checked={gcBillingForm}
            onCheckedChange={toggleGcBillingForm}
            data-testid="gc-billing-checkbox"
          />
          <Label htmlFor="gc-billing-form" className="cursor-pointer text-sm font-medium">
            Use GC Supplied Billing Form
          </Label>
        </div>
      )}

      {/* Summary Cards */}
      <BillingSummaryCards
        totalBilled={totalBilled}
        totalReceived={totalReceived}
        totalOutstanding={totalOutstanding}
        remainingToBill={gcBillingForm ? remainingToBill : null}
      />

      {/* Billings Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Billings</CardTitle>
          {canManageJobs && (
            <Button
              size="sm"
              onClick={gcBillingForm ? () => openGcDialog() : handleAddBilling}
              data-testid="add-billing-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {gcBillingForm ? "New Billing" : "New Application"}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {sortedBillings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>{gcBillingForm ? "#" : "App #"}</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Date Sent</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="w-52 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedBillings.map((billing) => (
                  <BillingTableRow
                    key={billing.id}
                    billing={billing}
                    canManageJobs={canManageJobs}
                    generatingExcel={generatingExcel}
                    revising={revising}
                    onRecordPayment={openPaymentDialog}
                    onMarkSent={openMarkSentDialog}
                    onEmailPayApp={handleEmailPayApp}
                    onDownloadExcel={downloadExcel}
                    onEdit={gcBillingForm ? (id) => {
                      const b = sortedBillings.find(x => x.id === id);
                      if (b) openGcDialog(b);
                    } : handleEditBilling}
                    onRevise={handleReviseBilling}
                    onDelete={onDeleteBilling}
                    gcBillingForm={gcBillingForm}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {gcBillingForm ? "No billings recorded yet" : "No payment applications yet"}
              </p>
              {canManageJobs && (
                <Button onClick={gcBillingForm ? () => openGcDialog() : handleAddBilling}>
                  <Plus className="w-4 h-4 mr-2" />
                  {gcBillingForm ? "Add First Billing" : "Create First Application"}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <PaymentHistorySection
        activeBillings={activeBillings}
        canManageJobs={canManageJobs}
        onDeletePayment={handleDeletePayment}
      />

      {/* Record Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        selectedBilling={selectedBilling}
        paymentForm={paymentForm}
        onPaymentFormChange={setPaymentForm}
        onSubmit={handleRecordPayment}
        isSubmitting={recordingPayment}
      />

      {/* Mark as Sent Dialog */}
      <MarkSentDialog
        open={markSentDialogOpen}
        onOpenChange={setMarkSentDialogOpen}
        selectedBilling={selectedBilling}
        form={markSentForm}
        onFormChange={setMarkSentForm}
        onSubmit={handleMarkSent}
        isSubmitting={markingSent}
      />

      {/* GC Billing Add/Edit Dialog */}
      <Dialog open={gcDialogOpen} onOpenChange={setGcDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGcBilling ? "Edit Billing" : "Add Billing"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="gc-amount">Billing Amount ($)</Label>
              <NumericInput
                id="gc-amount"
                decimals={2}
                min={0}
                value={gcForm.amount}
                onChange={(e) => setGcForm({ ...gcForm, amount: e.target.value })}
                placeholder="Enter billing amount"
                className="text-lg"
                data-testid="gc-billing-amount-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gc-date">Date Sent</Label>
              <Input
                id="gc-date"
                type="date"
                value={gcForm.date_sent}
                onChange={(e) => setGcForm({ ...gcForm, date_sent: e.target.value })}
                data-testid="gc-billing-date-input"
              />
            </div>
            {!editingGcBilling && remainingToBill > 0 && (
              <p className="text-sm text-muted-foreground">
                Remaining to bill: <span className="font-medium">${remainingToBill.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGcDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGcSubmit} disabled={submittingGc} data-testid="gc-billing-submit-btn">
              {submittingGc ? "Saving..." : editingGcBilling ? "Update" : "Add Billing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingsTab;
